import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { api } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import CommentItem from './CommentItem';

export default function CommentSection({ onRequireAuth }) {
  const { user, token } = useAuth();
  const [comments, setComments] = useState([]);
  const [sort, setSort] = useState('recent');
  const [content, setContent] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [likedIds, setLikedIds] = useState(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem('tk_liked') || '[]'));
    } catch { return new Set(); }
  });

  const load = useCallback(async () => {
    try {
      const data = await api(`/api/comments?sort=${sort === 'likes' ? 'likes' : 'recent'}`);
      setComments(data);
    } catch (e) {
      setError('Failed to load comments');
    }
  }, [sort]);

  useEffect(() => { load(); }, [load]);

  const topLikedId = useMemo(() => {
    if (!comments.length) return null;
    const max = comments.reduce((m, c) => (c.likes > m.likes ? c : m), comments[0]);
    return max.likes > 0 ? max._id : null;
  }, [comments]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!user) {
      onRequireAuth();
      return;
    }
    const text = content.trim();
    if (!text) return;
    setBusy(true);
    try {
      await api('/api/comments', {
        method: 'POST',
        body: { content: text },
        token,
      });
      setContent('');
      await load();
    } catch (e) {
      setError(e.message || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const handleLike = async (id) => {
    if (likedIds.has(id)) return;
    try {
      const data = await api(`/api/comments/${id}/like`, { method: 'POST' });
      setComments(prev => prev.map(c => c._id === id ? { ...c, likes: data.likes } : c));
      const next = new Set(likedIds);
      next.add(id);
      setLikedIds(next);
      localStorage.setItem('tk_liked', JSON.stringify(Array.from(next)));
    } catch (e) {
      if (e.status === 409) {
        const next = new Set(likedIds);
        next.add(id);
        setLikedIds(next);
        localStorage.setItem('tk_liked', JSON.stringify(Array.from(next)));
      }
    }
  };

  const handleDelete = async (id) => {
    if (!token) return;
    if (!window.confirm('Delete this comment?')) return;
    try {
      await api(`/api/comments/${id}`, { method: 'DELETE', token });
      setComments(prev => prev.filter(c => c._id !== id));
    } catch (e) {
      alert(e.message || 'Failed to delete');
    }
  };

  const handleReply = async (id, text) => {
    if (!token) { onRequireAuth(); return; }
    try {
      await api(`/api/comments/${id}/replies`, { method: 'POST', body: { content: text }, token });
      await load();
    } catch (e) {
      alert(e.message || 'Failed to reply');
    }
  };

  const handleDeleteReply = async (commentId, replyId) => {
    if (!token) return;
    if (!window.confirm('Delete this reply?')) return;
    try {
      await api(`/api/comments/${commentId}/replies/${replyId}`, { method: 'DELETE', token });
      await load();
    } catch (e) {
      alert(e.message || 'Failed to delete');
    }
  };

  return (
    <section className="comments">
      <div className="comments-header">
        <h2>Comments ({comments.length})</h2>
        <div className="sort-toggle">
          <button
            className={sort === 'recent' ? 'active' : ''}
            onClick={() => setSort('recent')}
          >Most Recent</button>
          <button
            className={sort === 'likes' ? 'active' : ''}
            onClick={() => setSort('likes')}
          >Most Likes</button>
        </div>
      </div>

      {user ? (
        <form className="comment-form" onSubmit={submit}>
          <textarea
            placeholder="Share your thoughts..."
            value={content}
            onChange={(e) => setContent(e.target.value)}
            maxLength={2000}
          />
          <div className="row">
            <span className="counter">{content.length}/2000</span>
            <button className="btn btn-primary btn-sm" type="submit" disabled={busy || !content.trim()}>
              {busy ? '...' : 'Post'}
            </button>
          </div>
          {error && <div className="error">{error}</div>}
        </form>
      ) : (
        <div className="login-prompt">
          <button onClick={onRequireAuth}>Login</button> to post a comment.
        </div>
      )}

      {comments.map((c) => (
        <CommentItem
          key={c._id}
          comment={c}
          isTopLiked={c._id === topLikedId}
          currentUser={user}
          liked={likedIds.has(c._id)}
          onLike={() => handleLike(c._id)}
          onDelete={() => handleDelete(c._id)}
          onReply={(text) => handleReply(c._id, text)}
          onDeleteReply={(rid) => handleDeleteReply(c._id, rid)}
          onRequireAuth={onRequireAuth}
        />
      ))}
    </section>
  );
}
